import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Stack, Check, BellSimple, BellSimpleSlash, ArrowRight } from "@phosphor-icons/react";

// Shows all volumes of a series and how many the current user owns.
export default function SeriesVolumes({ identifier }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!identifier) return;
    api.get(`/manga/${identifier}/series`).then(r => { setData(r.data); setFollowing(!!r.data.is_following); }).catch(() => setData(null));
  }, [identifier]);

  async function toggleFollow() {
    if (!user) { toast.error("Entra para seguir séries"); navigate("/login"); return; }
    setBusy(true);
    try {
      if (following) { await api.delete(`/me/series-follow/${data.series_id}`); setFollowing(false); toast.success("Deixaste de seguir"); }
      else { await api.post(`/me/series-follow/${data.series_id}`); setFollowing(true); toast.success("A seguir! Serás avisado de novos volumes."); }
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (!data || data.single || !data.volumes?.length) return null;

  const pct = data.total ? Math.round((data.owned_count / data.total) * 100) : 0;

  return (
    <div className="mt-2" data-testid="series-volumes">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="font-mono-accent text-xs text-zinc-500 flex items-center gap-2">
          <Stack size={16} weight="bold" className="text-[#E63946]" /> SÉRIE · {data.series_name}
        </div>
        {data.series_id && (
          <div className="flex items-center gap-2">
            <button onClick={toggleFollow} disabled={busy} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${following ? "bg-white/10 text-white hover:bg-white/15" : "bg-[#E63946] text-white hover:bg-[#c92f3c]"}`} data-testid="series-follow-btn">
              {following ? <><BellSimpleSlash size={13} weight="bold" /> A seguir</> : <><BellSimple size={13} weight="bold" /> Seguir série</>}
            </button>
            <Link to={`/serie/${data.series_slug}`} className="inline-flex items-center gap-1 text-xs text-[#E63946] hover:underline font-display font-semibold" data-testid="series-page-link">Ver série <ArrowRight size={12} weight="bold" /></Link>
          </div>
        )}
      </div>
      <div className="rounded-2xl border border-white/5 bg-[#0f0f14] p-5">
        <div className="flex items-end justify-between mb-2">
          <div className="font-display text-lg font-bold">
            {data.owned_count}<span className="text-zinc-500"> / {data.total}</span> <span className="text-sm text-zinc-400 font-normal">volumes na tua coleção</span>
          </div>
          <div className="font-mono-accent text-xs text-zinc-500" data-testid="series-progress-pct">{pct}%</div>
        </div>
        <div className="h-2.5 rounded-full bg-white/5 overflow-hidden mb-5">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#E63946,#7209B7)" }} />
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
          {data.volumes.map((v) => (
            <Link key={v.manga_id} to={`/manga/${v.slug || v.manga_id}`} className="group relative" data-testid={`series-vol-${v.manga_id}`} title={v.title}>
              <div className={`aspect-[2/3] overflow-hidden rounded-lg border ${v.owned ? "border-emerald-500/50" : "border-white/5"} bg-[#121217] relative`}>
                {v.cover_url ? (
                  <img src={v.cover_url} alt={v.title} className={`w-full h-full object-cover transition-opacity ${v.owned ? "" : "opacity-40 group-hover:opacity-70"}`} />
                ) : (
                  <div className={`w-full h-full grid place-items-center text-[10px] font-mono-accent text-zinc-600 ${v.owned ? "" : "opacity-50"}`}>{v.volume ? `#${v.volume}` : "—"}</div>
                )}
                {v.owned && (
                  <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 grid place-items-center">
                    <Check size={12} weight="bold" className="text-white" />
                  </div>
                )}
              </div>
              {v.volume != null && <div className="mt-1 text-center font-mono-accent text-[10px] text-zinc-500">Vol. {v.volume}</div>}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
